import Phaser from 'phaser'

export interface TimerDisplayConfig {
  x: number
  y: number
  duration: number // in seconds
  onComplete?: () => void
  countDown?: boolean
  warningThreshold?: number // seconds remaining to show warning
}

/**
 * Reusable timer display component
 */
export class TimerDisplay extends Phaser.GameObjects.Container {
  private timerText: Phaser.GameObjects.Text
  private bg: Phaser.GameObjects.Rectangle
  private duration: number
  private elapsed: number = 0
  private countDown: boolean
  private warningThreshold: number
  private onComplete?: () => void
  private timerEvent?: Phaser.Time.TimerEvent
  private isPaused: boolean = false
  private isComplete: boolean = false

  constructor(scene: Phaser.Scene, config: TimerDisplayConfig) {
    super(scene, config.x, config.y)

    this.duration = config.duration
    this.countDown = config.countDown ?? true
    this.warningThreshold = config.warningThreshold ?? 10
    this.onComplete = config.onComplete

    // Background
    this.bg = scene.add.rectangle(0, 0, 120, 50, 0x000000, 0.5)
    this.bg.setStrokeStyle(2, 0xffffff, 0.3)

    // Timer icon
    const icon = scene.add.text(-50, -8, '⏱', {
      fontSize: '20px',
    })
    icon.setOrigin(0, 0.5)

    // Timer value
    this.timerText = scene.add.text(50, -8, this.formatTime(this.countDown ? this.duration : 0), {
      fontFamily: 'system-ui',
      fontSize: '24px',
      fontStyle: 'bold',
      color: '#ffffff',
    })
    this.timerText.setOrigin(1, 0.5)

    this.add([this.bg, icon, this.timerText])
    scene.add.existing(this)
  }

  /**
   * Start the timer
   */
  start(): void {
    if (this.timerEvent) {
      this.timerEvent.destroy()
    }

    this.elapsed = 0
    this.isComplete = false
    this.isPaused = false

    this.timerEvent = this.scene.time.addEvent({
      delay: 100, // Update every 100ms for smooth display
      callback: this.tick,
      callbackScope: this,
      loop: true,
    })
  }

  /**
   * Pause the timer
   */
  pause(): void {
    this.isPaused = true
    if (this.timerEvent) {
      this.timerEvent.paused = true
    }
  }

  /**
   * Resume the timer
   */
  resume(): void {
    this.isPaused = false
    if (this.timerEvent) {
      this.timerEvent.paused = false
    }
  }

  /**
   * Stop the timer
   */
  stop(): void {
    if (this.timerEvent) {
      this.timerEvent.destroy()
      this.timerEvent = undefined
    }
  }

  /**
   * Get elapsed time in seconds
   */
  getElapsed(): number {
    return this.elapsed
  }

  /**
   * Get remaining time in seconds
   */
  getRemaining(): number {
    return Math.max(0, this.duration - this.elapsed)
  }

  /**
   * Check if timer is complete
   */
  getIsComplete(): boolean {
    return this.isComplete
  }

  private tick(): void {
    if (this.isPaused || this.isComplete) return

    this.elapsed += 0.1

    const displayTime = this.countDown
      ? Math.max(0, this.duration - this.elapsed)
      : this.elapsed

    this.timerText.setText(this.formatTime(displayTime))

    // Warning state
    if (this.countDown && this.getRemaining() <= this.warningThreshold) {
      this.timerText.setColor('#ff4444')
      this.bg.setFillStyle(0x441111, 0.7)

      // Pulse animation when low
      if (Math.floor(this.getRemaining()) !== Math.floor(this.getRemaining() + 0.1)) {
        this.scene.tweens.add({
          targets: this.timerText,
          scale: 1.1,
          duration: 100,
          yoyo: true,
        })
      }
    }

    // Check completion
    if (this.countDown && this.elapsed >= this.duration) {
      this.isComplete = true
      this.stop()
      this.onComplete?.()
    }
  }

  private formatTime(seconds: number): string {
    const mins = Math.floor(seconds / 60)
    const secs = Math.floor(seconds % 60)
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }

  destroy(fromScene?: boolean): void {
    this.stop()
    super.destroy(fromScene)
  }
}
